import axios, { AxiosInstance, AxiosError } from 'axios';
import i18n from '@/i18n';

const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
};

export const API_BASE_URL = getApiBaseUrl();

const IDIOMAS_DA_API: Record<string, string> = { pt: 'pt-BR', en: 'en', es: 'es' };

/** Língua atual da loja no formato que a API entende. Português se não houver escolha. */
export function apiLanguage(): string {
  const atual = (i18n.resolvedLanguage || i18n.language || 'pt').split('-')[0];
  return IDIOMAS_DA_API[atual] ?? 'pt-BR';
}

export class ApiClient {
  private client: AxiosInstance;
  /** No editor do construtor: sessão vencida não recarrega a página nem apaga o jogador salvo. */
  private noEditor = false;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
      // Sends HttpOnly cookies automatically on every request
      withCredentials: true,
    });

    /*
     * Língua das mensagens da API.
     *
     * A API traduz erros e avisos pelo Accept-Language. Sem este cabeçalho,
     * valia a língua do navegador: com o Chrome em inglês, o jogador via
     * "Data already registered in this store." mesmo com a loja em português.
     * Agora vai a língua escolhida na loja.
     */
    this.client.interceptors.request.use((config) => {
      config.headers = config.headers ?? {};
      (config.headers as any)['Accept-Language'] = apiLanguage();
      return config;
    });

    // Response interceptor: handle 401 with cookie-based auto-refresh for players
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<any>) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && this.noEditor) return Promise.reject(error);

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const storeId = localStorage.getItem('playerStoreId');

            if (storeId) {
              // RT cookie is sent automatically by the browser via withCredentials
              await axios.post(
                `${API_BASE_URL}/public/stores/${storeId}/players/refresh`,
                {},
                { withCredentials: true, headers: { 'Accept-Language': apiLanguage() } },
              );

              // New AT cookie set by server — retry the original request
              return this.client(originalRequest);
            }
          } catch {
            // Refresh failed — clear non-sensitive player data and reload
            localStorage.removeItem('playerStoreId');
            localStorage.removeItem('player');
            window.location.reload();
          }
        }

        // 401 with no refresh path — clear cached data
        if (error.response?.status === 401) {
          localStorage.removeItem('playerStoreId');
          localStorage.removeItem('player');
        }

        if (error.response?.data?.message) {
          const backendError = new Error(error.response.data.message);
          (backendError as any).response = error.response;
          (backendError as any).status = error.response.status;
          return Promise.reject(backendError);
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * Só no editor do construtor (`ModoConstrutor`): as rotas que `responder`
   * reconhece não vão para a rede e respondem com o dado de exemplo (ou com o
   * status de erro pedido); as outras seguem normais. É o que deixa as
   * páginas da conta aparecerem no editor sem jogador logado.
   */
  usarAmostras(responder: (metodo: string, url: string) => { status: number; data?: unknown } | undefined): void {
    this.noEditor = true;
    const padrao = axios.getAdapter(this.client.defaults.adapter);
    this.client.defaults.adapter = async (config) => {
      const resposta = responder((config.method || 'get').toLowerCase(), config.url || '');
      if (!resposta) return padrao(config);
      const corpo = { data: resposta.data ?? {}, status: resposta.status, statusText: String(resposta.status), headers: {}, config, request: {} };
      if (resposta.status >= 400) throw new AxiosError(`amostra ${resposta.status}`, String(resposta.status), config, {}, corpo as never);
      return corpo as never;
    };
  }

  async get<T>(url: string, config?: any): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  getBaseURL(): string {
    return API_BASE_URL;
  }
}

export const apiClient = new ApiClient();
